/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Grid, Typography, useTheme, useMediaQuery } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { ICustomTheme } from 'interfaces';

type Theme = {
  theme?: ICustomTheme;
};

const Condition = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.white,
  fontSize: theme.typography.body1.fontSize,
  '&.marginBottom': {
    marginBottom: theme.spacing(3),
  },
}));

const ConditionTitle = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
  fontSize: theme.typography.h4.fontSize,
  marginBottom: theme.spacing(2),
}));

const GroupTab = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.grey.dark,
  fontSize: theme.typography.body2.fontSize,
  marginBottom: theme.spacing(2),
  '&.active': {
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: 'underline',
  },
}));

const LevelLogo = styled(Skeleton)<Theme>(({ theme }) => ({
  height: 72,
  marginBottom: theme.spacing(1),
  width: 72,
}));

const Step = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.grey.dark,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightBold,
}));

const StepperContainer = styled.div<Theme>(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginBottom: theme.spacing(5),
}));

const Text = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
  fontSize: theme.typography.body2.fontSize,
  '&.bold': {
    fontWeight: theme.typography.fontWeightBold,
  },
  '&.info': {
    color: theme.colors.grey.dark,
  },
  '&.white': {
    color: theme.colors.white,
  },
}));

const GenericGoalsSkeleton = (): JSX.Element => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid container item direction={isMobile ? 'column' : 'row'} spacing={4}>
      <Grid container item direction="column" lg={7}>
        <Grid container item justify="space-around">
          <Skeleton>
            <GroupTab className="active">
              {t('challenges.detail.goals_and_rewards.goals.group', {
                groupName: 'Limonest',
              })}
            </GroupTab>
          </Skeleton>
          <Skeleton>
            <GroupTab>
              {t('challenges.detail.goals_and_rewards.goals.group', {
                groupName: 'Paris',
              })}
            </GroupTab>
          </Skeleton>
          <Skeleton>
            <GroupTab>
              {t('challenges.detail.goals_and_rewards.goals.group', {
                groupName: 'Marseille',
              })}
            </GroupTab>
          </Skeleton>
        </Grid>

        <Grid
          container
          item
          direction={isMobile ? 'column' : 'row'}
          justify="space-around"
        >
          {[1, 2, 3].map((step) => (
            <StepperContainer key={step}>
              <Skeleton>
                <Step>
                  {t('challenges.detail.goals_and_rewards.goals.stage', {
                    nb: step,
                  })}
                </Step>
              </Skeleton>
              <Skeleton>
                <Text
                  className="bold"
                  css={{
                    marginBottom: theme.spacing(1),
                  }}
                >
                  {t(
                    'challenges.detail.goals_and_rewards.skeleton.rewards.points',
                    { nb: `${step} 000` },
                  )}
                </Text>
              </Skeleton>

              <LevelLogo variant="circle" />

              <Skeleton>
                <Text className="bold">
                  {t(
                    'challenges.detail.goals_and_rewards.skeleton.goals.action',
                    { nb: `${step}0 000` },
                  )}
                </Text>
              </Skeleton>
              <Skeleton>
                <Text>
                  {t('challenges.detail.goals_and_rewards.skeleton.goals.unit')}
                </Text>
              </Skeleton>
            </StepperContainer>
          ))}
        </Grid>
      </Grid>

      <Grid container item direction="column" lg={5}>
        <Skeleton>
          <ConditionTitle>
            {t('challenges.detail.goals_and_rewards.conditions.title')}
          </ConditionTitle>
        </Skeleton>

        <Grid
          container
          item
          direction="column"
          css={{
            padding: theme.spacing(2),
            backgroundColor: theme.colors.paletteFromBO.main,
          }}
        >
          <Skeleton>
            <Condition className="marginBottom">
              {t(
                'challenges.detail.goals_and_rewards.skeleton.conditions.first',
              )}
            </Condition>
          </Skeleton>
          <Skeleton>
            <Condition className="marginBottom">
              {t(
                'challenges.detail.goals_and_rewards.skeleton.conditions.second',
              )}
            </Condition>
          </Skeleton>
          <Skeleton>
            <Condition>
              {t(
                'challenges.detail.goals_and_rewards.skeleton.conditions.third',
              )}
            </Condition>
          </Skeleton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default GenericGoalsSkeleton;
